import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wrench, Loader2, AlertTriangle } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";
import { Logo } from "@/components/Logo";

const DEFAULT_MSG = "We're polishing the gold. The customer panel is under scheduled maintenance and will be back shortly.";
const toLocal = (iso) => (iso ? iso.slice(0, 16) : "");

export default function AdminMaintenance() {
  const { refreshMaintenance } = useOutletContext();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    aapi.get("/maintenance").then(({ data }) =>
      setForm({
        enabled: data.enabled,
        message: data.message || DEFAULT_MSG,
        scheduled_start: toLocal(data.scheduled_start),
        scheduled_end: toLocal(data.scheduled_end),
      })
    );
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await aapi.put("/maintenance", {
        enabled: form.enabled,
        message: form.message,
        scheduled_start: form.scheduled_start ? new Date(form.scheduled_start).toISOString() : null,
        scheduled_end: form.scheduled_end ? new Date(form.scheduled_end).toISOString() : null,
      });
      toast.success(form.enabled ? "Maintenance mode ON — customer panel is now blocked" : "Maintenance mode OFF — customer panel restored");
      refreshMaintenance().catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!form)
    return (
      <div className="flex justify-center py-32">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold/20 border-t-gold" />
      </div>
    );

  return (
    <div data-testid="admin-maintenance-page">
      <PageHeader title="Maintenance Mode" sub="Freeze the customer panel instantly. This console stays live." />

      <div className="grid gap-6 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Master switch</div>
              <div className="mt-1 text-lg font-semibold text-neutral-100">Maintenance mode</div>
            </div>
            <button
              onClick={() => setForm({ ...form, enabled: !form.enabled })}
              data-testid="maintenance-toggle"
              aria-label="Toggle maintenance mode"
              className={`relative h-8 w-14 rounded-full transition-colors duration-300 ${form.enabled ? "bg-amber-400" : "bg-white/10"}`}
            >
              <span className={`absolute top-1 h-6 w-6 rounded-full bg-white transition-all duration-300 ${form.enabled ? "left-7" : "left-1"}`} />
            </button>
          </div>

          {form.enabled && (
            <p className="mt-4 flex gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-xs text-amber-300" data-testid="maintenance-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              Customers will see the maintenance page the moment you save. The admin console is never affected.
            </p>
          )}

          <div className="mt-6">
            <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Maintenance message</label>
            <textarea rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${inputCls} mt-2 resize-none`} data-testid="maintenance-message-input" />
          </div>

          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Scheduled start (optional)</label>
              <input type="datetime-local" value={form.scheduled_start}
                onChange={(e) => setForm({ ...form, scheduled_start: e.target.value })}
                className={`${inputCls} mt-2 [color-scheme:dark]`} data-testid="maintenance-start-input" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Scheduled end (optional)</label>
              <input type="datetime-local" value={form.scheduled_end}
                onChange={(e) => setForm({ ...form, scheduled_end: e.target.value })}
                className={`${inputCls} mt-2 [color-scheme:dark]`} data-testid="maintenance-end-input" />
            </div>
          </div>

          <button onClick={save} disabled={busy} data-testid="maintenance-save-button"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.12em] text-black disabled:opacity-50">
            {busy && <Loader2 size={15} className="animate-spin" />}
            SAVE — APPLIES INSTANTLY
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">What customers will see</div>
          <div className="relative mt-3 overflow-hidden rounded-3xl border border-gold/25 bg-[#08080B] p-10 text-center" data-testid="maintenance-preview">
            <div className="pointer-events-none absolute left-1/2 top-0 h-48 w-72 -translate-x-1/2 rounded-full bg-gold/10 blur-[80px]" />
            <Logo className="relative mx-auto h-14 w-14 rounded-2xl object-cover ring-1 ring-gold/40" />
            <div className="relative mt-5 flex items-center justify-center gap-2 font-mono text-[10px] tracking-[0.3em] text-amber-300">
              <Wrench size={12} /> UNDER MAINTENANCE
            </div>
            <p className="relative mx-auto mt-4 max-w-sm font-serif text-xl italic leading-relaxed text-neutral-200">
              "{form.message || DEFAULT_MSG}"
            </p>
            <p className="relative mt-4 font-mono text-[9px] tracking-[0.25em] text-neutral-600">BEATKINGSMM 2.0 · BACK SHORTLY</p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
